import type { PdfConfirmRequest } from '../../types/pdfPreview';

export type PendingPdfConfirm = {
  previewId: string;
  idempotencyKey: string;
  request: PdfConfirmRequest;
  confirmId: string | null;
};

const storageKey = (previewId: string) => `onlineproperty.pdf-confirm.${previewId}`;

export const hasPendingPdfConfirmRecord = (previewId: string): boolean => {
  try {
    return window.sessionStorage.getItem(storageKey(previewId)) !== null;
  } catch {
    return true;
  }
};

export const newPdfConfirmKey = (): string => {
  if (!window.crypto?.getRandomValues) throw new Error('Secure random generator unavailable');
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return `pdf_${Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')}`;
};

export const savePendingPdfConfirm = (operation: PendingPdfConfirm): void => {
  // Only submitted operation metadata is retained. No PDF, token, S3 key or Blob URL is stored.
  window.sessionStorage.setItem(storageKey(operation.previewId), JSON.stringify(operation));
};

export const readPendingPdfConfirm = (previewId: string): PendingPdfConfirm | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(previewId));
    if (!raw) return null;
    const entry: unknown = JSON.parse(raw);
    if (!entry || typeof entry !== 'object') return null;
    const candidate = entry as Partial<PendingPdfConfirm>;
    if (
      candidate.previewId !== previewId ||
      typeof candidate.idempotencyKey !== 'string' ||
      !/^[A-Za-z0-9_-]{8,128}$/.test(candidate.idempotencyKey) ||
      (candidate.confirmId !== null && typeof candidate.confirmId !== 'string') ||
      !candidate.request ||
      !Array.isArray(candidate.request.projectOverrides) ||
      !Array.isArray(candidate.request.decisions) ||
      typeof candidate.request.acknowledgePartial !== 'boolean'
    )
      return null;
    return candidate as PendingPdfConfirm;
  } catch {
    return null;
  }
};

export const clearPendingPdfConfirm = (previewId: string): void => {
  window.sessionStorage.removeItem(storageKey(previewId));
};
